/**
 * 공통 캐시 서비스
 * 메모리 기반 캐시 + TTL 지원
 * 여러 서비스에서 재사용 가능
 */

/**
 * @typedef {Object} CacheItem
 * @property {*} data - 캐시된 데이터
 * @property {number} timestamp - 캐시 저장 시간
 * @property {number} ttl - TTL (milliseconds)
 */

class CacheService {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5분
    this.maxSize = 1000; // 최대 캐시 항목 수
    
    // 주기적 정리 (10분마다)
    this.cleanupInterval = setInterval(() => this.cleanup(), 10 * 60 * 1000);
  }

  /**
   * 캐시 키 생성 (prefix + 실제 키)
   * @param {string} prefix - 서비스 구분자
   * @param {string} key - 실제 키
   * @returns {string}
   */
  createKey(prefix, key) {
    return `${prefix}:${key}`;
  }

  /**
   * 데이터 저장
   * @param {string} key - 캐시 키
   * @param {*} data - 저장할 데이터
   * @param {number} [ttl] - TTL (기본값: defaultTTL)
   */
  set(key, data, ttl = this.defaultTTL) {
    // 캐시 크기 제한
    if (this.cache.size >= this.maxSize) {
      this.cleanup();
      
      // 여전히 크면 가장 오래된 항목 제거
      if (this.cache.size >= this.maxSize) {
        const oldestKey = this.cache.keys().next().value;
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * 데이터 조회
   * @param {string} key - 캐시 키
   * @returns {*|null} 캐시된 데이터 또는 null
   */
  get(key) {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // TTL 확인
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * 데이터 존재 여부 확인
   * @param {string} key - 캐시 키
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * 특정 키 삭제
   * @param {string} key - 캐시 키
   */
  delete(key) {
    this.cache.delete(key);
  }

  /**
   * prefix 기반 삭제
   * @param {string} prefix - 삭제할 prefix
   */
  deleteByPrefix(prefix) {
    const prefixWithColon = `${prefix}:`;
    const keysToDelete = [];
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefixWithColon)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * 만료된 항목 정리
   */
  cleanup() {
    const now = Date.now();
    const keysToDelete = [];
    
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * 전체 캐시 초기화
   */
  clear() {
    this.cache.clear();
  }

  /**
   * 캐시 통계 조회
   * @returns {Object} 캐시 통계
   */
  getStats() {
    const now = Date.now();
    let expiredCount = 0;
    
    for (const item of this.cache.values()) {
      if (now - item.timestamp > item.ttl) {
        expiredCount++;
      }
    }
    
    return {
      totalItems: this.cache.size,
      expiredItems: expiredCount,
      activeItems: this.cache.size - expiredCount,
      maxSize: this.maxSize
    };
  }

  /**
   * 메모리 정리 (서비스 종료 시)
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
  }
}

// 전역 캐시 인스턴스
export const cacheService = new CacheService();
export default cacheService;